/**
 * Note Chat Layout
 *
 * Presentation layer template for Note AI Edit feature.
 * Handles 3-column layout structure with slots for content components.
 *
 * Layout Structure:
 * - Left: Chat sidebar (450px fixed width, animated slide-in/out)
 * - Middle: Note sections (flex: 1, scrollable)
 * - Right: Table of contents (sticky, integrated in middle section)
 *
 * Responsive Behavior:
 * - Auto-hides chat sidebar on screens narrower than 1280px (lg breakpoint)
 * - Animates sidebar with smooth slide transitions
 */

import React, { useEffect, useRef } from 'react';
import { useContainerQuery } from '@hooks/useContainerQuery';
import { useStickyHeaderDetection } from '@hooks/useStickyHeaderDetection';
import { Box, Container } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import {
  LAYOUT_ANIMATIONS,
  LAYOUT_DIMENSIONS,
  SECTION_GUTTER_PADDING,
} from '../../constants/layout';

export interface NoteChatLayoutProps {
  /**
   * Chat sidebar component (left column)
   * Should include all chat UI: messages, input, status
   * Optional - when undefined, chat sidebar is hidden for certain view modes
   */
  chat?: React.ReactNode;

  /**
   * Controls chat sidebar visibility and animation
   * When false, chat slides out; when true, chat slides in
   * @default true
   */
  chatVisible?: boolean;

  /**
   * Optional approval banner (sticky at top of sections)
   * Appears above note content when AI changes need approval
   */
  banner?: React.ReactNode;

  /**
   * Top navigation bar (sticky at top of sections)
   * Contains template selector, actions, etc.
   */
  topBar: React.ReactNode;

  /**
   * Note metadata section
   * Patient assignment, encounter info, etc.
   */
  metadata: React.ReactNode;

  /**
   * Optional tabs navigation (between metadata and content)
   * Used for switching between different view modes (e.g., Note/Transcript)
   */
  tabs?: React.ReactNode;

  /**
   * Note title editor
   * Editable heading for the note
   */
  noteTitle: React.ReactNode;

  /**
   * Optional JotCoach overview component
   * Displays compliance analysis and quality scores
   */
  jotCoachOverview?: React.ReactNode;

  /**
   * Note sections list
   * Main content area with editable sections
   */
  noteSections: React.ReactNode;

  /**
   * Table of contents sidebar (left side, sticky)
   * Navigation for sections - line indicators with hover popover
   * Used on md breakpoint (769-1024px)
   */
  noteToc: React.ReactNode;

  /**
   * Inline table of contents (left side, sticky, always visible)
   * Full section list without popover
   * Used on lg breakpoint (≥1025px)
   */
  noteTocInline?: React.ReactNode;

  /**
   * Alternative content (replaces sections + TOC)
   * Used for views like transcript that replace the main note content
   * When provided, noteSections and noteToc are not rendered
   */
  alternativeContent?: React.ReactNode;

  /**
   * ID for the scroll container (for programmatic scrolling)
   * @default 'note-sections-scroll-container'
   */
  scrollContainerId?: string;

  /**
   * Optional overlay content (e.g., onboarding overlays)
   * Rendered with absolute positioning covering the main content area
   * Automatically inherits Container centering on ultrawide screens
   */
  overlay?: React.ReactNode;

  /**
   * Whether the mobile drawer is currently open (for narrow screens)
   * Used to add bottom padding to allow scrolling past the drawer
   */
  mobileDrawerOpen?: boolean;

  /**
   * Height of the mobile drawer as percentage of viewport height
   * Used to calculate bottom padding when drawer is open
   */
  mobileDrawerHeight?: number;
}

/**
 * NoteChatLayout - Presentation template for Note AI Edit
 *
 * Separates layout concerns from business logic by providing a slot-based
 * composition pattern. Each slot receives a fully-functional component.
 *
 * Responsibilities:
 * - 3-column flexbox structure
 * - Chat sidebar dimensions and styling
 * - Scroll container setup
 * - Background colors and borders
 * - Height and overflow management
 *
 * @example
 * ```tsx
 * // Edit mode with chat
 * <NoteChatLayout
 *   chat={<NoteChat {...chatProps} />}
 *   banner={<ApprovalBanner {...bannerProps} />}
 *   topBar={<NoteTopBarEdit {...topBarProps} />}
 *   metadata={<NoteMetadata {...metadataProps} />}
 *   noteTitle={<NoteTitleEditor {...titleProps} />}
 *   noteSections={<NoteSectionsList {...sectionsProps} />}
 *   noteToc={<NoteTOC {...tocProps} />}
 * />
 *
 * // Review mode without chat
 * <NoteChatLayout
 *   topBar={<NoteTopBarReview {...topBarProps} />}
 *   metadata={<NoteMetadata {...metadataProps} />}
 *   noteTitle={<NoteTitleEditor {...titleProps} />}
 *   noteSections={<NoteSectionsList {...sectionsProps} />}
 *   noteToc={<NoteTOC {...tocProps} />}
 * />
 * ```
 */
export const NoteChatLayout: React.FC<NoteChatLayoutProps> = ({
  chat,
  chatVisible = true,
  banner,
  topBar,
  metadata,
  tabs,
  noteTitle,
  jotCoachOverview,
  noteSections,
  noteToc,
  noteTocInline,
  alternativeContent,
  scrollContainerId = 'note-sections-scroll-container',
  overlay,
  mobileDrawerOpen = false,
  mobileDrawerHeight = 0,
}) => {
  const theme = useTheme();

  // Sticky header detection for TOC
  const { isStuck, registerSentinel } = useStickyHeaderDetection();
  const isTOCStuck = isStuck('note-toc');
  const tocRef = useRef<HTMLElement>(null);

  // Smoothly scroll TOC to top when it becomes unstuck
  useEffect(() => {
    if (!isTOCStuck && tocRef.current) {
      tocRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }, [isTOCStuck]);

  // Responsive breakpoints for scroll container
  // xs/sm: NoteTOCButton in top bar
  // md (769-1024px): Line indicators with hover popover
  // lg (≥1025px): Always-visible full TOC list
  const { ref: scrollContainerRef, breakpoint } = useContainerQuery<HTMLDivElement>({
    xs: { maxWidth: 480 },
    sm: { minWidth: 481, maxWidth: 768 },
    md: { minWidth: 769, maxWidth: 1024 },
    lg: { minWidth: 1025, maxWidth: 1280 },
    xl: { minWidth: 1281, maxWidth: 1440 },
    xxl: { minWidth: 1441 },
  });

  // Show line indicator TOC on md breakpoint only
  const showInlineTOC = breakpoint === 'md';
  // Show full inline TOC list on lg breakpoint onwards
  const showInlineTOCList = breakpoint === 'lg' || breakpoint === 'xl' || breakpoint === 'xxl';
  // Wide container is xl or xxl breakpoints
  const isUltraWideContainer = breakpoint === 'xxl';
  const isSmallContainer = breakpoint === 'xs' || breakpoint === 'sm';

  return (
    <Container
      disableGutters
      maxWidth="xxl"
      sx={{
        position: 'relative',
        display: 'flex',
        height: '100%',
        overflow: 'hidden',
        flexDirection: 'row',
        borderLeft: `1px solid ${theme.palette.divider}`,
        borderRight: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Container
        disableGutters
        maxWidth="xxl"
        ref={scrollContainerRef}
        id={scrollContainerId}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          // Reduce height when mobile drawer is open to allow scrolling past drawer
          height: mobileDrawerOpen ? `${100 - mobileDrawerHeight}vh` : '100%',
          maxWidth: '100%',
          flex: 1,
          overflow: 'scroll',
          // CSS Container Query setup
          containerType: 'inline-size',
          'container-name': 'note-sections-container',
          transition: 'height 0.2s ease',
        }}
      >
        {/* Sticky Header Group - All header elements stick together */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: theme.zIndex.appBar - 1,
            bgcolor: 'background.paper',
          }}
        >
          {/* Metadata - Patient assignment and encounter info */}
          {metadata}

          {/* Optional Approval Banner */}
          {banner}

          {/* Top Bar - Navigation and actions */}
          {topBar}

          {/* Optional Tabs Navigation - View mode switcher */}
          {tabs}
        </Box>

        {/* Note Sections + TOC */}
        <Box
          sx={{
            bgcolor: theme.palette.background.default,
            height: '100%',
            display: 'flex',
            maxWidth: '100%',
          }}
        >
          {/* Content Row: Alternative Content OR (Sections + TOC) */}
          {alternativeContent ? (
            /* Alternative content (e.g., transcript) - full width without TOC */
            <Box sx={{ flex: 1, py: 6 }}>{alternativeContent}</Box>
          ) : (
            /* Normal note view - Sections + TOC */
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                height: 'fit-content',
                flex: 1,
                maxWidth: '100%',
              }}
            >
              {/* TOC Left Column - Fixed width, sticky content */}
              {(showInlineTOC || showInlineTOCList) && (
                <Box
                  sx={{
                    flexShrink: 0,
                    width: showInlineTOCList
                      ? LAYOUT_DIMENSIONS.TOC_INLINE_MAX_WIDTH
                      : LAYOUT_DIMENSIONS.TOC_WIDTH,
                  }}
                >
                  {/* Sentinel element for sticky detection */}
                  <Box
                    ref={(el: HTMLElement | null) => registerSentinel('note-toc', el)}
                    data-header-id="note-toc"
                    sx={{
                      height: '1px',
                      marginTop: '5px',
                      visibility: 'hidden',
                    }}
                  />
                  <Box
                    ref={tocRef}
                    sx={{
                      position: 'sticky',
                      top: showInlineTOCList
                        ? LAYOUT_DIMENSIONS.TOP_BAR_HEIGHT
                        : LAYOUT_DIMENSIONS.TOC_STICKY_TOP,
                      ml: showInlineTOCList ? 3 : 2,
                      mt: 5,
                      // Account for global Nav height (64px) + feature top bar (64px)
                      maxHeight: `calc(100vh - ${LAYOUT_DIMENSIONS.TOP_BAR_HEIGHT}px - ${theme.layout.navHeight.sm}px)`,
                      // Only enable scroll when TOC is stuck to save vertical space
                      overflowY: isTOCStuck ? 'auto' : 'visible',
                    }}
                  >
                    {showInlineTOCList ? noteTocInline : noteToc}
                  </Box>
                </Box>
              )}

              {/* Main Content Column - Flex to fill */}
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  paddingLeft: isSmallContainer ? 0 : `${SECTION_GUTTER_PADDING}px`,
                  paddingRight: isSmallContainer
                    ? 0
                    : !isUltraWideContainer || chatVisible
                      ? `clamp(0px, 3cqw, ${SECTION_GUTTER_PADDING}px)`
                      : `${LAYOUT_DIMENSIONS.TOC_INLINE_MAX_WIDTH}px`,

                  transition: `padding-right ${LAYOUT_ANIMATIONS.CHAT_SLIDE_DURATION}ms ${LAYOUT_ANIMATIONS.CHAT_SLIDE_EASING}`,
                  overflow: 'hidden',
                }}
              >
                {/* Note Title */}
                {noteTitle}

                {/* JotCoach Overview */}
                {jotCoachOverview}

                {/* Sections List */}
                {noteSections}
              </Box>
            </Box>
          )}
        </Box>
      </Container>

      {/* Chat Sidebar - Always rendered when chat exists, animated based on visibility */}
      {chat && (
        <Box
          data-chat-sidebar-container
          sx={{
            position: 'sticky',
            top: 0,
            right: 0,
            width: chatVisible ? LAYOUT_DIMENSIONS.CHAT_SIDEBAR_WIDTH : 0,
            opacity: chatVisible ? 1 : 0,
            height: '100%',
            flexShrink: 0,
            flexGrow: 0,
            overflow: 'hidden',
            transition: `width ${LAYOUT_ANIMATIONS.CHAT_SLIDE_DURATION}ms ${LAYOUT_ANIMATIONS.CHAT_SLIDE_EASING}, opacity ${LAYOUT_ANIMATIONS.CHAT_SLIDE_DURATION}ms ${LAYOUT_ANIMATIONS.CHAT_SLIDE_EASING}`,
          }}
        >
          <Box
            sx={{
              width: LAYOUT_DIMENSIONS.CHAT_SIDEBAR_WIDTH,
              transform: chatVisible
                ? 'translateX(0)'
                : `translateX(${LAYOUT_DIMENSIONS.CHAT_SIDEBAR_WIDTH}px)`,
              transition: `transform ${LAYOUT_ANIMATIONS.CHAT_SLIDE_DURATION}ms ${LAYOUT_ANIMATIONS.CHAT_SLIDE_EASING}`,
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              overflow: 'hidden',
              bgcolor: theme.palette.chat.colors.chatBackground,
              borderLeft: `1px solid ${theme.palette.divider}`,
              // Prevent interaction when hidden
              pointerEvents: chatVisible ? 'auto' : 'none',
            }}
          >
            {chat}
          </Box>
        </Box>
      )}

      {/* Optional overlay (e.g., onboarding) - absolute positioned within container */}
      {overlay}
    </Container>
  );
};
